import React, { useState, useEffect, useRef } from "react"
import styles from "./Org.module.scss"
import { UserInterface } from "../../App"
import axios from "axios"
import { API_ROUTE } from "../../utils"
import toast from "react-hot-toast"

interface OrgInterface {
    _id: string
    name: string
    owner: string
    created: number
    members: { userId: string; role: string; joined: number }[]
    settings: object
}

interface Member {
    userId: string
    email: string
    name: string
    role: string
    joined: number
}

const Org: React.FC<{
    user: UserInterface
    setUser: React.Dispatch<React.SetStateAction<UserInterface>>
}> = ({ user, setUser }) => {
    const [orgs, setOrgs] = useState<OrgInterface[]>([])
    const [selectedOrgId, setSelectedOrgId] = useState<string>(user.activeOrgId || "")
    const [members, setMembers] = useState<Member[]>([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [inviteEmail, setInviteEmail] = useState("")
    const [orgName, setOrgName] = useState("")
    const modalRef = useRef<HTMLDivElement>(null)

    // Fetch orgs
    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const response = await axios.get(`${API_ROUTE}/orgs/user`, {
                    params: { owner: user._id, authToken: user.authToken },
                })
                setOrgs(response.data.orgs)
                setSelectedOrgId(
                    response.data.activeOrgId ||
                        user.activeOrgId ||
                        response.data.orgs[0]?._id ||
                        ""
                )
                setUser({
                    ...user,
                    activeOrgId:
                        response.data.activeOrgId || user.activeOrgId || response.data.orgs[0]?._id,
                })
            } catch (err) {
                console.error("Failed to fetch organizations:", err)
                if (user.orgs?.length > 0) {
                    setOrgs(
                        user.orgs.map((orgId) => ({
                            _id: orgId,
                            name: "Loading...",
                            owner: user._id,
                            created: Date.now(),
                            members: [],
                            settings: {},
                        }))
                    )
                    setSelectedOrgId(user.activeOrgId || user.orgs[0])
                }
            }
        }
        fetchOrgs()
    }, [user._id, user.authToken, user.orgs, user.activeOrgId, setUser])

    // Fetch members when selected org changes
    useEffect(() => {
        if (selectedOrgId) {
            const fetchMembers = async () => {
                try {
                    const response = await axios.get(`${API_ROUTE}/orgs/${selectedOrgId}/members`, {
                        params: { owner: user._id, authToken: user.authToken },
                    })
                    setMembers(response.data.members)
                } catch (err) {
                    console.error("Failed to fetch members:", err)
                }
            }
            fetchMembers()
        }
    }, [selectedOrgId, user._id, user.authToken])

    // Handle org selection
    const handleOrgChange = async (orgId: string) => {
        setSelectedOrgId(orgId)
        try {
            await axios.post(`${API_ROUTE}/orgs/set-active`, {
                orgId,
                owner: user._id,
                authToken: user.authToken,
            })
            setUser({ ...user, activeOrgId: orgId })
            toast.success("Switched to " + orgs.find((org) => org._id === orgId)?.name)
        } catch (err) {
            alert("There was an error setting the active organization")
        }
    }

    // Handle clicks outside modal
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                setIsModalOpen(false)
                setInviteEmail("")
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    const handleCreateOrg = async () => {
        if (!orgName) {
            alert("Please enter an organization name")
            return
        }
        try {
            const response = await axios.post(`${API_ROUTE}/orgs/create`, {
                name: orgName,
                owner: user._id,
                authToken: user.authToken,
            })
            const newOrg = {
                _id: response.data.orgId,
                name: orgName,
                owner: user._id,
                created: Date.now(),
                members: [{ userId: user._id, role: "owner", joined: Date.now() }],
                settings: {},
            }
            setOrgs([...orgs, newOrg])
            setSelectedOrgId(newOrg._id)
            setUser({ ...user, orgs: [...(user.orgs || []), newOrg._id], activeOrgId: newOrg._id })
            await axios.post(`${API_ROUTE}/orgs/set-active`, {
                orgId: newOrg._id,
                owner: user._id,
                authToken: user.authToken,
            })
            setOrgName("")
            toast.success("Switched to " + newOrg.name)
        } catch (err) {
            alert("There was an error creating the organization")
        }
    }

    const handleInviteMember = async () => {
        if (!inviteEmail) {
            alert("Email is required to invite a member")
            return
        }
        try {
            await axios.post(`${API_ROUTE}/orgs/${selectedOrgId}/members`, {
                email: inviteEmail,
                role: "member",
                owner: user._id,
                authToken: user.authToken,
            })
            const response = await axios.get(`${API_ROUTE}/orgs/${selectedOrgId}/members`, {
                params: { owner: user._id, authToken: user.authToken },
            })
            setMembers(response.data.members)
            setIsModalOpen(false)
            setInviteEmail("")
        } catch (err) {
            alert("We couldn't invite this user. Please check the email address and try again.")
        }
    }

    const selectedOrg = orgs.find((org) => org._id === selectedOrgId)

    return (
        <div className={styles.Org}>
            <div className={styles.container}>
                <h2 className={styles.heading}>Organization</h2>
                {orgs.length > 1 ? (
                    <>
                        <p className={styles.textMuted}>
                            Select an organization to manage its members and settings.
                        </p>
                        <br />
                    </>
                ) : (
                    <br />
                )}
                {orgs.length > 0 && (
                    <select
                        value={selectedOrgId}
                        onChange={(e) => handleOrgChange(e.target.value)}
                        className={styles.select}>
                        {orgs.map((org) => (
                            <option key={org._id} value={org._id}>
                                {org.name}
                            </option>
                        ))}
                    </select>
                )}
                <hr />
                {selectedOrg && (
                    <>
                        <div className={styles.orgDetails}>
                            <h3 className={styles.subheading}>{selectedOrg.name}</h3>
                            <p className={styles.textMuted}>
                                Created: {new Date(selectedOrg.created).toLocaleDateString()}
                            </p>
                        </div>
                        <div>
                            <h4 className={styles.subheading}>Members</h4>
                            <table className={styles.table}>
                                <thead>
                                    <tr className={styles.tableHeader}>
                                        <th className={styles.tableCell}>Name</th>
                                        <th className={styles.tableCell}>Email</th>
                                        <th className={styles.tableCell}>Role</th>
                                        <th className={styles.tableCell}>Joined</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((member) => (
                                        <tr key={member.userId} className={styles.tableRow}>
                                            <td className={styles.tableCell}>{member.name}</td>
                                            <td className={styles.tableCell}>{member.email}</td>
                                            <td className={styles.tableCell}>{member.role}</td>
                                            <td className={styles.tableCell}>
                                                {new Date(member.joined).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div onClick={() => setIsModalOpen(true)} className={styles.addMember}>
                            <i className="material-symbols-rounded">person_add</i>
                            Invite Member
                        </div>
                    </>
                )}
            </div>

            <hr />

            <div className={styles.container}>
                <h2 className={styles.heading}>Create an Organization</h2>

                <br />

                <input
                    type="text"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Organization Name"
                    className={styles.input}
                />
                <button onClick={handleCreateOrg}>Create Organization</button>
            </div>

            {isModalOpen && (
                <div className={styles.modalOverlay}>
                    <div ref={modalRef} className={styles.modal}>
                        <h3 className={styles.subheading}>Invite Member</h3>
                        <br />
                        <input
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="Member Email"
                            className={styles.input}
                        />
                        <div className={styles.modalActions}>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false)
                                    setInviteEmail("")
                                }}
                                className={styles.buttonCancel}>
                                Cancel
                            </button>
                            <button onClick={handleInviteMember}>Invite</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Org
